import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {ChevronDown, ChevronRight, CheckCircle, XCircle, Loader2, Send, Trash2} from 'lucide-react';
import FortisService from '@/shared/services/fortisService';
import type {SandboxStatusResponse} from '@/shared/services/fortisService';
import Button from '@/shared/components/Button';

// ---------------------------------------------------------------------------
// Collapsible Section
// ---------------------------------------------------------------------------

interface SectionProps {
    title: string;
    description?: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}

function Section({title, description, children, defaultOpen = false}: SectionProps) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="border border-mld-panel-border rounded-lg bg-white overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-mld-menu-background transition-colors"
            >
                <div>
                    <h3 className="text-base font-semibold text-mld-text-primary">{title}</h3>
                    {description && <p className="text-sm text-mld-muted-text mt-0.5">{description}</p>}
                </div>
                {open ? (
                    <ChevronDown className="w-5 h-5 text-mld-muted-text flex-shrink-0" />
                ) : (
                    <ChevronRight className="w-5 h-5 text-mld-muted-text flex-shrink-0" />
                )}
            </button>
            {open && <div className="px-5 pb-5 border-t border-mld-panel-border">{children}</div>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// JSON Response Display
// ---------------------------------------------------------------------------

function parseFortisError(error: string): {title: string; details: string[]} | null {
    // Try to extract the JSON portion from "Fortis API ... status NNN: {json}"
    const jsonMatch = error.match(/\{.*\}$/s);
    if (!jsonMatch) return null;
    try {
        const parsed = JSON.parse(jsonMatch[0]);
        const title = parsed.title || 'Error';
        const details: string[] = [];

        // Extract from meta.errors (field-level errors)
        if (parsed.meta?.errors) {
            for (const [field, msgs] of Object.entries(parsed.meta.errors)) {
                const msgList = Array.isArray(msgs) ? msgs : [msgs];
                for (const m of msgList) {
                    details.push(`${field}: ${m}`);
                }
            }
        }

        // Extract from meta.details (validation errors)
        if (parsed.meta?.details && Array.isArray(parsed.meta.details)) {
            for (const d of parsed.meta.details) {
                if (d.message) details.push(d.message.replace(/"/g, ''));
            }
        }

        // Fallback to detail string
        if (details.length === 0 && parsed.detail) {
            details.push(parsed.detail);
        }

        return {title, details};
    } catch {
        return null;
    }
}

function ResponseDisplay({data, error, loading}: {data?: unknown; error?: string | null; loading?: boolean}) {
    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-mld-muted-text py-3">
                <Loader2 className="w-4 h-4 animate-spin" /> Executing...
            </div>
        );
    }

    if (error) {
        const parsed = parseFortisError(error);
        return (
            <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-md">
                <p className="text-sm font-medium text-rose-700">{parsed?.title || 'Error'}</p>
                {parsed && parsed.details.length > 0 ? (
                    <ul className="mt-1 space-y-0.5">
                        {parsed.details.map((d, i) => (
                            <li key={i} className="text-sm text-rose-600">{d}</li>
                        ))}
                    </ul>
                ) : (
                    <pre className="text-xs text-rose-600 mt-1 whitespace-pre-wrap break-all">{error}</pre>
                )}
            </div>
        );
    }

    if (data !== undefined && data !== null) {
        return (
            <div className="mt-3 p-3 bg-mld-menu-background border border-mld-panel-border rounded-md">
                <p className="text-sm font-medium text-mld-text-secondary mb-1">Response</p>
                <pre className="text-xs text-mld-text-secondary whitespace-pre-wrap break-all max-h-96 overflow-y-auto">
                    {JSON.stringify(data, null, 2)}
                </pre>
            </div>
        );
    }

    return null;
}

// ---------------------------------------------------------------------------
// Form Field helpers
// ---------------------------------------------------------------------------

function FormField({
    label,
    value,
    onChange,
    placeholder,
    type = 'text',
    disabled,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    type?: string;
    disabled?: boolean;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className={`w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm text-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary ${disabled ? 'opacity-50 bg-mld-menu-background cursor-not-allowed' : ''}`}
            />
        </div>
    );
}

function TextAreaField({
    label,
    value,
    onChange,
    placeholder,
    rows = 4,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    rows?: number;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                className="w-full px-3 py-2 border border-mld-panel-border rounded-md shadow-sm text-sm font-mono focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
            />
        </div>
    );
}

function ExecuteButton({
    onClick,
    loading,
    label = 'Execute',
    variant = 'primary',
}: {
    onClick: () => void;
    loading?: boolean;
    label?: string;
    variant?: 'primary' | 'danger';
}) {
    const baseClasses = 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors';
    const variantClasses =
        variant === 'danger'
            ? 'bg-rose-600 text-white hover:bg-rose-700'
            : 'bg-mld-admin-primary text-white hover:bg-mld-admin-primary-dark';

    return (
        <button type="button" onClick={onClick} disabled={loading} className={`${baseClasses} ${variantClasses}`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {label}
        </button>
    );
}

// ---------------------------------------------------------------------------
// Generic action hook
// ---------------------------------------------------------------------------

function useEndpointAction(
    fn: (body: Record<string, unknown>) => Promise<unknown>
): {
    execute: (body: Record<string, unknown>) => void;
    data: unknown;
    error: string | null;
    loading: boolean;
    reset: () => void;
} {
    const [data, setData] = useState<unknown>(null);
    const [error, setError] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: fn,
        onSuccess: (result) => {
            setData(result);
            setError(null);
        },
        onError: (err: any) => {
            setError(err?.data?.error || err?.message || 'Request failed');
            setData(null);
        },
    });

    const reset = useCallback(() => {
        setData(null);
        setError(null);
    }, []);

    return {
        execute: (body) => mutation.mutate(body),
        data,
        error,
        loading: mutation.isPending,
        reset,
    };
}

// ---------------------------------------------------------------------------
// Section: Connection Status
// ---------------------------------------------------------------------------

function ConnectionStatusSection() {
    const {data, isLoading, error, refetch} = useQuery({
        queryKey: ['fortis', 'sandbox', 'status'],
        queryFn: FortisService.getSandboxStatus,
    });

    const status = data as SandboxStatusResponse | undefined;

    return (
        <Section title="Connection Status" description="Test API connectivity and view credentials" defaultOpen={true}>
            <div className="pt-4 space-y-3">
                {isLoading && (
                    <div className="flex items-center gap-2 text-sm text-mld-muted-text">
                        <Loader2 className="w-4 h-4 animate-spin" /> Checking connection...
                    </div>
                )}
                {error && <p className="text-sm text-rose-600">Failed to check status</p>}
                {status && (
                    <>
                        <div className="flex items-center gap-2">
                            {status.connected ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                                <XCircle className="w-5 h-5 text-rose-500" />
                            )}
                            <span className="text-sm font-medium">
                                {status.connected ? 'Connected to Fortis' : 'Not connected'}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="text-mld-muted-text">Base URL:</div>
                            <div className="font-mono text-xs">{status.baseUrl}</div>
                            {status.locationId && (
                                <>
                                    <div className="text-mld-muted-text">Location ID:</div>
                                    <div className="font-mono text-xs">{status.locationId}</div>
                                </>
                            )}
                            <div className="text-mld-muted-text">User ID:</div>
                            <div>{status.credentials.userId ? '  configured' : '  missing'}</div>
                            <div className="text-mld-muted-text">API Key:</div>
                            <div>{status.credentials.apiKey ? '  configured' : '  missing'}</div>
                            <div className="text-mld-muted-text">Developer ID:</div>
                            <div>{status.credentials.developerId ? '  configured' : '  missing'}</div>
                        </div>
                        {status.error && <p className="text-sm text-rose-600 mt-2">{status.error}</p>}
                        <button
                            onClick={() => refetch()}
                            className="text-sm text-mld-admin-primary hover:text-mld-admin-primary-dark font-medium"
                        >
                            Refresh
                        </button>
                    </>
                )}
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Contacts
// ---------------------------------------------------------------------------

function ContactsSection() {
    const [action, setAction] = useState('create');
    const [contactId, setContactId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const endpoint = useEndpointAction(FortisService.sandboxContacts);

    const handleExecute = () => {
        const body: Record<string, unknown> = {action};
        if (action === 'get' || action === 'update') {
            body.contactId = contactId;
        }
        if (action === 'create' || action === 'update') {
            body.data = {
                ...(firstName && {first_name: firstName}),
                ...(lastName && {last_name: lastName}),
                ...(email && {email}),
            };
        }
        endpoint.execute(body);
    };

    return (
        <Section title="Contacts" description="Create, search, and view Fortis contacts">
            <div className="pt-4 space-y-4">
                <div className="flex gap-2">
                    {(['create', 'get', 'update', 'list'] as const).map((a) => (
                        <button
                            key={a}
                            onClick={() => {
                                setAction(a);
                                endpoint.reset();
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                action === a
                                    ? 'bg-mld-admin-primary text-white'
                                    : 'bg-mld-menu-background text-mld-text-secondary hover:bg-mld-panel-border'
                            }`}
                        >
                            {a.charAt(0).toUpperCase() + a.slice(1)}
                        </button>
                    ))}
                </div>

                {(action === 'get' || action === 'update') && (
                    <FormField label="Contact ID" value={contactId} onChange={setContactId} placeholder="Contact ID" />
                )}
                {(action === 'create' || action === 'update') && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <FormField label="First Name" value={firstName} onChange={setFirstName} />
                        <FormField label="Last Name" value={lastName} onChange={setLastName} />
                        <FormField label="Email" value={email} onChange={setEmail} type="email" />
                    </div>
                )}

                <ExecuteButton onClick={handleExecute} loading={endpoint.loading} />
                <ResponseDisplay data={endpoint.data} error={endpoint.error} loading={endpoint.loading} />
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Transactions
// ---------------------------------------------------------------------------

interface TxnSummary {
    id: string;
    account_holder_name: string;
    transaction_amount: number;
    surcharge_amount: number;
    payment_method: string;
    last_four: string;
    status_code: number;
    type_id: number;
    created_ts: number;
    verbiage: string;
}

/** Fortis type_id values where the amount represents money going back to the customer */
const NEGATIVE_TYPE_IDS = new Set([30, 40]); // 30 = refund, 40 = void

// ---------------------------------------------------------------------------
// Fortis Status Code Map
// ---------------------------------------------------------------------------

const FORTIS_STATUS_CODES: Record<number, {label: string; category: 'approved' | 'ach-pending' | 'ach-settled' | 'voided' | 'declined' | 'chargeback'}> = {
    101: {label: 'Sale CC Approved', category: 'approved'},
    102: {label: 'Sale CC AuthOnly', category: 'approved'},
    111: {label: 'Refund CC Refunded', category: 'approved'},
    121: {label: 'AVS Only', category: 'approved'},
    131: {label: 'ACH Pending Origination', category: 'ach-pending'},
    132: {label: 'ACH Originating', category: 'ach-pending'},
    133: {label: 'ACH Originated', category: 'ach-pending'},
    134: {label: 'ACH Settled', category: 'ach-settled'},
    201: {label: 'Voided', category: 'voided'},
    301: {label: 'Declined', category: 'declined'},
    331: {label: 'ACH Charged Back', category: 'chargeback'},
};

const ACH_STATUS_PIPELINE = [
    {code: 131, label: 'Pending', shortLabel: 'Pending'},
    {code: 132, label: 'Originating', shortLabel: 'Originating'},
    {code: 133, label: 'Originated', shortLabel: 'Originated'},
    {code: 134, label: 'Settled', shortLabel: 'Settled'},
];

function getStatusBadgeClasses(statusCode: number): string {
    const info = FORTIS_STATUS_CODES[statusCode];
    if (!info) return 'bg-mld-menu-background text-mld-text-secondary';
    switch (info.category) {
        case 'approved':
        case 'ach-settled':
            return 'bg-green-100 text-green-800';
        case 'ach-pending':
            return 'bg-blue-100 text-blue-800';
        case 'voided':
            return 'bg-amber-100 text-amber-800';
        case 'declined':
        case 'chargeback':
            return 'bg-rose-100 text-rose-800';
        default:
            return 'bg-mld-menu-background text-mld-text-secondary';
    }
}

function getStatusLabel(statusCode: number, verbiage?: string): string {
    const info = FORTIS_STATUS_CODES[statusCode];
    if (info) return info.label;
    return verbiage || `Status ${statusCode}`;
}

function isAchStatus(statusCode: number): boolean {
    return [131, 132, 133, 134, 331].includes(statusCode);
}

// ---------------------------------------------------------------------------
// ACH Status Pipeline (visual tracker)
// ---------------------------------------------------------------------------

function AchStatusPipeline({
    statusCode,
    onRefresh,
    refreshing,
    onAdvanceStatus,
    advancing,
}: {
    statusCode: number;
    onRefresh?: () => void;
    refreshing?: boolean;
    onAdvanceStatus?: (nextStatusId: number) => void;
    advancing?: boolean;
}) {
    const isChargeback = statusCode === 331;
    const isDeclined = statusCode === 301;
    const isTerminal = statusCode === 134 || isChargeback || isDeclined;

    // Calculate the next status in the pipeline
    const currentIdx = ACH_STATUS_PIPELINE.findIndex((s) => s.code === statusCode);
    const nextStep = currentIdx >= 0 && currentIdx < ACH_STATUS_PIPELINE.length - 1
        ? ACH_STATUS_PIPELINE[currentIdx + 1]
        : null;

    return (
        <div className="p-4 bg-mld-menu-background rounded-lg border border-mld-panel-border space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-mld-text-secondary">ACH Status Tracker</h4>
                <div className="flex items-center gap-2">
                    {onRefresh && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onRefresh}
                            disabled={refreshing || advancing}
                            loading={refreshing}>
                            Refresh
                        </Button>
                    )}
                    {onAdvanceStatus && nextStep && !isTerminal && (
                        <button
                            type="button"
                            onClick={() => onAdvanceStatus(nextStep.code)}
                            disabled={advancing || refreshing}
                            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                        >
                            {advancing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            Sandbox: Advance to {nextStep.shortLabel} ({nextStep.code})
                        </button>
                    )}
                    {onAdvanceStatus && !isTerminal && statusCode >= 131 && statusCode <= 133 && (
                        <button
                            type="button"
                            onClick={() => onAdvanceStatus(331)}
                            disabled={advancing || refreshing}
                            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md bg-rose-500 text-white hover:bg-rose-600 transition-colors"
                        >
                            {advancing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            Sandbox: Chargeback (331)
                        </button>
                    )}
                </div>
            </div>

            {/* Pipeline steps */}
            <div className="flex items-center gap-0">
                {ACH_STATUS_PIPELINE.map((step, idx) => {
                    const isActive = statusCode === step.code;
                    const isPast = statusCode > step.code && statusCode <= 134;

                    let dotClass = 'bg-mld-muted-text/40';
                    let labelClass = 'text-mld-muted-text';
                    let connectorClass = 'bg-mld-panel-border';

                    if (isActive && step.code === 134) {
                        dotClass = 'bg-green-500 ring-4 ring-green-100';
                        labelClass = 'text-green-700 font-semibold';
                    } else if (isActive) {
                        dotClass = 'bg-blue-500 ring-4 ring-blue-100';
                        labelClass = 'text-blue-700 font-semibold';
                    } else if (isPast) {
                        dotClass = 'bg-green-500';
                        labelClass = 'text-green-700';
                        connectorClass = 'bg-green-400';
                    }

                    return (
                        <React.Fragment key={step.code}>
                            {idx > 0 && (
                                <div className={`flex-1 h-1 rounded ${isPast ? connectorClass : 'bg-mld-panel-border'}`} />
                            )}
                            <div className="flex flex-col items-center" style={{minWidth: '80px'}}>
                                <div className={`w-4 h-4 rounded-full ${dotClass} transition-all`} />
                                <span className={`mt-1.5 text-xs ${labelClass} whitespace-nowrap`}>
                                    {step.shortLabel}
                                </span>
                                <span className="text-[10px] text-mld-muted-text">{step.code}</span>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Chargeback / Declined alert */}
            {isChargeback && (
                <div className="flex items-center gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-md">
                    <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                    <span className="text-sm text-rose-700 font-medium">ACH Charged Back (331)</span>
                </div>
            )}
            {isDeclined && (
                <div className="flex items-center gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-md">
                    <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                    <span className="text-sm text-rose-700 font-medium">ACH Declined (301)</span>
                </div>
            )}

            <p className="text-xs text-mld-muted-text">
                {isTerminal
                    ? 'This transaction has reached a terminal state.'
                    : 'Use the sandbox buttons to simulate ACH status progression, or click Refresh to re-fetch the current status from Fortis.'}
            </p>
        </div>
    );
}

function formatCents(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
}

function formatTxnDate(ts: number): string {
    if (!ts) return '';
    return new Date(ts * 1000).toLocaleString();
}

function txnLabel(t: TxnSummary): string {
    const name = t.account_holder_name || 'Unknown';
    const isNeg = NEGATIVE_TYPE_IDS.has(t.type_id);
    const amt = isNeg ? `-${formatCents(t.transaction_amount)}` : formatCents(t.transaction_amount);
    const last4 = t.last_four ? ` ****${t.last_four}` : '';
    return `${name}${last4} — ${amt}`;
}

function TransactionPicker({
    label,
    transactions,
    value,
    onChange,
}: {
    label: string;
    transactions: TxnSummary[];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            {transactions.length > 0 ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                >
                    <option value="">Select a transaction...</option>
                    {transactions.map((t) => (
                        <option key={t.id} value={t.id}>
                            {txnLabel(t)} — {getStatusLabel(t.status_code, t.verbiage)}
                        </option>
                    ))}
                </select>
            ) : (
                <p className="text-sm text-mld-muted-text italic">
                    No transactions loaded. Click &quot;list&quot; first to fetch transactions.
                </p>
            )}
            {value && (
                <p className="mt-1 text-xs text-mld-muted-text font-mono truncate">{value}</p>
            )}
        </div>
    );
}

function TransactionsSection({onTxnsFetched}: {onTxnsFetched?: (txns: TxnSummary[]) => void}) {
    const [action, setAction] = useState('list');
    const [transactionId, setTransactionId] = useState('');
    const [selectedTokenId, setSelectedTokenId] = useState('');
    const [amount, setAmount] = useState('100');
    const [refundAmount, setRefundAmount] = useState('');
    const [achRefundAmount, setAchRefundAmount] = useState('');

    const [fetchedTxns, setFetchedTxns] = useState<TxnSummary[]>([]);
    const [data, setData] = useState<unknown>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [viewedStatusCode, setViewedStatusCode] = useState<number | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [advancing, setAdvancing] = useState(false);

    const savedTokens = loadSavedTokens();
    const isTokenAction = ['cc-sale', 'ach-debit-token'].includes(action);
    const needsTxnPicker = ['get', 'refund-cc', 'refund-ach', 'void'].includes(action);

    // Auto-select first token if none selected
    const effectiveTokenId = selectedTokenId || (savedTokens.length > 0 ? savedTokens[0].tokenId : '');

    const handleExecute = async () => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const body: Record<string, unknown> = {action};

            switch (action) {
                case 'cc-sale':
                case 'ach-debit-token':
                    body.data = {
                        account_vault_id: effectiveTokenId,
                        transaction_amount: Number(amount) || 100,
                    };
                    break;
                case 'get':
                    body.transactionId = transactionId;
                    break;
                case 'list':
                    break;
                case 'refund-cc': {
                    body.transactionId = transactionId;
                    const refAmt = Number(refundAmount);
                    const refData: Record<string, unknown> = {};
                    if (refAmt > 0) {
                        refData.transaction_amount = refAmt;
                        refData.subtotal_amount = refAmt;
                    }
                    if (Object.keys(refData).length > 0) {
                        body.data = refData;
                    }
                    break;
                }
                case 'refund-ach':
                    body.data = {
                        previous_transaction_id: transactionId,
                        ...(achRefundAmount ? {transaction_amount: Number(achRefundAmount)} : {}),
                    };
                    break;
                case 'void':
                    body.transactionId = transactionId;
                    break;
            }

            const result = await FortisService.sandboxTransactions(body);
            setData(result);

            // Extract status_code from single-transaction responses (get, sale, debit)
            if (action !== 'list') {
                const d = (result as any)?.data || result;
                const sc = d?.status_code;
                if (typeof sc === 'number') {
                    setViewedStatusCode(sc);
                } else {
                    setViewedStatusCode(null);
                }
            }

            // Extract transaction list when list response arrives
            if (action === 'list') {
                const raw = result as any;
                const list: any[] = raw?.list || raw?.data || (Array.isArray(raw) ? raw : []);
                const summaries: TxnSummary[] = list.map((t: any) => ({
                    id: t.id || '',
                    account_holder_name: t.account_holder_name || '',
                    transaction_amount: t.transaction_amount || 0,
                    surcharge_amount: t.surcharge_amount || 0,
                    payment_method: t.payment_method || '',
                    last_four: t.last_four || '',
                    status_code: t.status_code || 0,
                    type_id: t.type_id || 0,
                    created_ts: t.created_ts || 0,
                    verbiage: t.verbiage || '',
                }));
                setFetchedTxns(summaries);
                onTxnsFetched?.(summaries);
                if (summaries.length > 0 && !transactionId) {
                    setTransactionId(summaries[0].id);
                }
            }
        } catch (err: any) {
            setError(err?.data?.error || err?.message || 'Request failed');
        } finally {
            setLoading(false);
        }
    };

    // Refresh a transaction to get updated status (useful for ACH status tracking)
    const handleRefreshStatus = async () => {
        if (!transactionId) return;
        setRefreshing(true);
        try {
            const result = await FortisService.sandboxTransactions({action: 'get', transactionId});
            setData(result);
            const d = (result as any)?.data || result;
            const sc = d?.status_code;
            if (typeof sc === 'number') setViewedStatusCode(sc);
        } catch (err: any) {
            setError(err?.data?.error || err?.message || 'Refresh failed');
        } finally {
            setRefreshing(false);
        }
    };

    // Sandbox-only: advance ACH status via PUT /v2/developer/transactions/{id}
    const handleAdvanceStatus = async (nextStatusId: number) => {
        if (!transactionId) return;
        setAdvancing(true);
        setError(null);
        try {
            const result = await FortisService.sandboxTransactions({
                action: 'developer-update-status',
                transactionId,
                data: {status_id: nextStatusId},
            });
            setData(result);
            // v2 gateway wraps in result.transaction with status_id (not status_code)
            const raw = result as any;
            const sc = raw?.transaction?.status_id ?? raw?.data?.status_code ?? raw?.data?.status_id;
            if (typeof sc === 'number') setViewedStatusCode(sc);
        } catch (err: any) {
            setError(err?.data?.error || err?.message || 'Failed to advance status');
        } finally {
            setAdvancing(false);
        }
    };

    const actions = ['list', 'cc-sale', 'ach-debit-token', 'get', 'refund-cc', 'refund-ach', 'void'];

    const renderListTable = () => {
        if (action !== 'list' || fetchedTxns.length === 0) return null;
        return (
            <div className="overflow-x-auto rounded-lg border border-mld-panel-border">
                <table className="w-full text-sm">
                    <thead className="bg-mld-menu-background text-left text-xs uppercase text-mld-muted-text">
                        <tr>
                            <th className="px-3 py-2">Name</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">Method</th>
                            <th className="px-3 py-2">Amount</th>
                            <th className="px-3 py-2">Surcharge</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mld-panel-border">
                        {fetchedTxns.map((t) => (
                            <tr key={t.id} className="hover:bg-mld-menu-background">
                                <td className="px-3 py-2 font-medium text-mld-text-primary">
                                    {t.account_holder_name || '—'}
                                </td>
                                <td className="px-3 py-2 text-mld-muted-text text-xs font-mono">
                                    {t.type_id}
                                </td>
                                <td className="px-3 py-2 text-mld-text-secondary">
                                    {(t.payment_method || '').toUpperCase()}
                                    {t.last_four ? ` ****${t.last_four}` : ''}
                                </td>
                                <td className={`px-3 py-2 font-mono ${NEGATIVE_TYPE_IDS.has(t.type_id) ? 'text-rose-600' : 'text-mld-text-primary'}`}>
                                    {NEGATIVE_TYPE_IDS.has(t.type_id) ? `-${formatCents(t.transaction_amount)}` : formatCents(t.transaction_amount)}
                                </td>
                                <td className="px-3 py-2 text-mld-muted-text font-mono text-xs">
                                    {t.surcharge_amount > 0 ? formatCents(t.surcharge_amount) : '—'}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${getStatusBadgeClasses(t.status_code)}`}>
                                        {getStatusLabel(t.status_code, t.verbiage)}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-mld-muted-text text-xs whitespace-nowrap">
                                    {formatTxnDate(t.created_ts)}
                                </td>
                                <td className="px-3 py-2 text-mld-muted-text font-mono text-xs truncate max-w-[140px]" title={t.id}>
                                    {t.id}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Section title="Transactions" description="Run CC sales, ACH debits, refunds, and voids">
            <div className="pt-4 space-y-4">
                <div className="flex gap-2 flex-wrap">
                    {actions.map((a) => (
                        <button
                            key={a}
                            onClick={() => {
                                setAction(a);
                                setData(null);
                                setError(null);
                                setViewedStatusCode(null);
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                action === a
                                    ? 'bg-mld-admin-primary text-white'
                                    : 'bg-mld-menu-background text-mld-text-secondary hover:bg-mld-panel-border'
                            }`}
                        >
                            {a}
                        </button>
                    ))}
                </div>

                {/* LIST — no params, shows table */}
                {action === 'list' && (
                    <p className="text-sm text-mld-muted-text">
                        Fetch recent transactions.
                        {fetchedTxns.length > 0 && ` ${fetchedTxns.length} transaction(s) loaded.`}
                    </p>
                )}

                {/* GET / VOID / REFUND-CC / REFUND-ACH — transaction picker dropdown */}
                {needsTxnPicker && (
                    <div className="space-y-3">
                        <TransactionPicker
                            label="Transaction"
                            transactions={fetchedTxns}
                            value={transactionId}
                            onChange={setTransactionId}
                        />
                        {action === 'void' && (
                            <p className="text-xs text-amber-600">Voiding a transaction cannot be undone.</p>
                        )}
                        {action === 'refund-cc' && (
                            <>
                                <FormField
                                    label="Refund Amount (cents, leave blank for full refund)"
                                    value={refundAmount}
                                    onChange={setRefundAmount}
                                    placeholder="e.g. 500 for $5.00"
                                    type="number"
                                />
                                <p className="text-xs text-mld-muted-text">
                                    Surcharge is detected and included automatically.
                                </p>
                            </>
                        )}
                        {action === 'refund-ach' && (
                            <FormField
                                label="Refund Amount (cents, leave blank for full refund)"
                                value={achRefundAmount}
                                onChange={setAchRefundAmount}
                                placeholder="e.g. 500 for $5.00"
                                type="number"
                            />
                        )}
                    </div>
                )}

                {/* Token-based actions: token dropdown + amount */}
                {isTokenAction && (
                    <div className="space-y-3">
                        <TokenPicker
                            label="Token"
                            tokens={action === 'ach-debit-token'
                                ? savedTokens.filter((t) => (t.paymentMethod || '').toLowerCase() === 'ach')
                                : savedTokens
                            }
                            value={effectiveTokenId}
                            onChange={setSelectedTokenId}
                        />
                        <FormField
                            label="Amount (cents)"
                            value={amount}
                            onChange={setAmount}
                            placeholder="100"
                            type="number"
                        />
                    </div>
                )}

                <ExecuteButton
                    onClick={handleExecute}
                    loading={loading}
                    variant={['refund-cc', 'refund-ach', 'void'].includes(action) ? 'danger' : 'primary'}
                />

                {renderListTable()}

                {/* ACH Status Pipeline — shown when viewing a transaction with ACH status */}
                {action !== 'list' && viewedStatusCode !== null && isAchStatus(viewedStatusCode) && (
                    <AchStatusPipeline
                        statusCode={viewedStatusCode}
                        onRefresh={transactionId ? handleRefreshStatus : undefined}
                        refreshing={refreshing}
                        onAdvanceStatus={transactionId ? handleAdvanceStatus : undefined}
                        advancing={advancing}
                    />
                )}

                {action !== 'list' && (
                    <ResponseDisplay data={data} error={error} loading={loading} />
                )}
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Recurring
// ---------------------------------------------------------------------------

interface RecurringSummary {
    id: string;
    account_vault_id: string;
    transaction_amount: number;
    interval_type: string;
    interval: number;
    start_date: string;
    end_date: string;
    status: string;
    payment_method: string;
    created_ts: number;
}

function recurringLabel(r: RecurringSummary): string {
    const amt = formatCents(r.transaction_amount);
    const freq = r.interval_type === 'm' ? 'Monthly' : r.interval_type === 'w' ? 'Weekly' : r.interval_type === 'd' ? 'Daily' : r.interval_type;
    return `${amt} ${freq} — ${r.status || 'unknown'}`;
}

function RecurringPicker({
    label,
    recurrings,
    value,
    onChange,
}: {
    label: string;
    recurrings: RecurringSummary[];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            {recurrings.length > 0 ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                >
                    <option value="">Select a recurring...</option>
                    {recurrings.map((r) => (
                        <option key={r.id} value={r.id}>
                            {recurringLabel(r)}
                        </option>
                    ))}
                </select>
            ) : (
                <p className="text-sm text-mld-muted-text italic">
                    No recurrings loaded. Click &quot;list&quot; first to fetch recurrings.
                </p>
            )}
            {value && (
                <p className="mt-1 text-xs text-mld-muted-text font-mono truncate">{value}</p>
            )}
        </div>
    );
}

function TokenPicker({
    label,
    tokens,
    value,
    onChange,
}: {
    label: string;
    tokens: SavedToken[];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            {tokens.length > 0 ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                >
                    <option value="">Select a token...</option>
                    {tokens.map((t) => (
                        <option key={t.tokenId} value={t.tokenId}>
                            {t.accountHolderName ? `${t.accountHolderName} — ` : ''}
                            {(t.paymentMethod || 'cc').toUpperCase()}
                            {t.lastFour ? ` ****${t.lastFour}` : ''}
                        </option>
                    ))}
                </select>
            ) : (
                <p className="text-sm text-mld-muted-text italic">
                    No saved tokens. Run a transaction with &quot;Save card info&quot; enabled in Elements.
                </p>
            )}
            {value && (
                <p className="mt-1 text-xs text-mld-muted-text font-mono truncate">{value}</p>
            )}
        </div>
    );
}

function RecurringSection() {
    const [action, setAction] = useState('list');
    const [recurringId, setRecurringId] = useState('');
    const [selectedTokenId, setSelectedTokenId] = useState('');
    const [amount, setAmount] = useState('5000');
    const [intervalType, setIntervalType] = useState('m');
    const [interval, setInterval] = useState('1');
    const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [jsonBody, setJsonBody] = useState('{}');
    const [fetchedRecurrings, setFetchedRecurrings] = useState<RecurringSummary[]>([]);
    const endpoint = useEndpointAction(FortisService.sandboxRecurrings);

    const savedTokens = loadSavedTokens();
    const effectiveTokenId = selectedTokenId || (savedTokens.length > 0 ? savedTokens[0].tokenId : '');

    // Extract recurring list when list response arrives
    useEffect(() => {
        if (action !== 'list' || !endpoint.data) return;
        const raw = endpoint.data as any;
        const list: any[] = raw?.list || raw?.data || (Array.isArray(raw) ? raw : []);
        const summaries: RecurringSummary[] = list.map((r: any) => ({
            id: r.id || '',
            account_vault_id: r.account_vault_id || '',
            transaction_amount: r.transaction_amount || 0,
            interval_type: r.interval_type || '',
            interval: r.interval || 0,
            start_date: r.start_date || '',
            end_date: r.end_date || '',
            status: r.status || '',
            payment_method: r.payment_method || '',
            created_ts: r.created_ts || 0,
        }));
        setFetchedRecurrings(summaries);
    }, [action, endpoint.data]);

    const needsRecurringPicker = ['get', 'update', 'delete'].includes(action);

    const handleExecute = () => {
        const body: Record<string, unknown> = {action};
        if (needsRecurringPicker) {
            body.recurringId = recurringId;
        }
        if (action === 'create') {
            body.data = {
                account_vault_id: effectiveTokenId,
                transaction_amount: Number(amount) || 5000,
                interval_type: intervalType,
                interval: Number(interval) || 1,
                start_date: startDate,
            };
        } else if (action === 'update') {
            try {
                body.data = JSON.parse(jsonBody);
            } catch {
                body.data = {};
            }
        }
        endpoint.execute(body);
    };

    const actions = ['list', 'create', 'get', 'update', 'delete'];

    const renderListTable = () => {
        if (action !== 'list' || fetchedRecurrings.length === 0) return null;
        return (
            <div className="overflow-x-auto rounded-lg border border-mld-panel-border">
                <table className="w-full text-sm">
                    <thead className="bg-mld-menu-background text-left text-xs uppercase text-mld-muted-text">
                        <tr>
                            <th className="px-3 py-2">Amount</th>
                            <th className="px-3 py-2">Frequency</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Start</th>
                            <th className="px-3 py-2">End</th>
                            <th className="px-3 py-2">Method</th>
                            <th className="px-3 py-2">ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mld-panel-border">
                        {fetchedRecurrings.map((r) => {
                            const freq = r.interval_type === 'm' ? 'Monthly' : r.interval_type === 'w' ? 'Weekly' : r.interval_type === 'd' ? 'Daily' : r.interval_type;
                            return (
                                <tr key={r.id} className="hover:bg-mld-menu-background">
                                    <td className="px-3 py-2 font-mono text-mld-text-primary">{formatCents(r.transaction_amount)}</td>
                                    <td className="px-3 py-2 text-mld-text-secondary">
                                        {freq}{r.interval > 1 ? ` (every ${r.interval})` : ''}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                                            r.status === 'active'
                                                ? 'bg-green-100 text-green-800'
                                                : r.status === 'on hold'
                                                ? 'bg-amber-100 text-amber-800'
                                                : 'bg-mld-menu-background text-mld-text-secondary'
                                        }`}>
                                            {r.status || '—'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-mld-text-secondary text-xs">{r.start_date || '—'}</td>
                                    <td className="px-3 py-2 text-mld-text-secondary text-xs">{r.end_date || '—'}</td>
                                    <td className="px-3 py-2 text-mld-text-secondary text-xs">{(r.payment_method || '').toUpperCase()}</td>
                                    <td className="px-3 py-2 text-mld-muted-text font-mono text-xs truncate max-w-[140px]" title={r.id}>{r.id}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderActionForm = () => {
        switch (action) {
            case 'list':
                return (
                    <p className="text-sm text-mld-muted-text">
                        Fetch all recurrings.
                        {fetchedRecurrings.length > 0 && ` ${fetchedRecurrings.length} recurring(s) loaded.`}
                    </p>
                );
            case 'create':
                return (
                    <div className="space-y-3 p-4 bg-mld-menu-background rounded-lg border border-mld-panel-border">
                        <TokenPicker
                            label="Token"
                            tokens={savedTokens}
                            value={selectedTokenId}
                            onChange={setSelectedTokenId}
                        />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <FormField label="Amount (cents)" value={amount} onChange={setAmount} type="number" />
                            <div>
                                <label className="block text-sm font-medium text-mld-text-secondary mb-1">Interval Type</label>
                                <select
                                    value={intervalType}
                                    onChange={(e) => setIntervalType(e.target.value)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                                >
                                    <option value="d">Daily</option>
                                    <option value="w">Weekly</option>
                                    <option value="m">Monthly</option>
                                </select>
                            </div>
                            <FormField label="Interval" value={interval} onChange={setInterval} type="number" />
                            <FormField label="Start Date" value={startDate} onChange={setStartDate} type="date" />
                        </div>
                    </div>
                );
            case 'get':
                return (
                    <RecurringPicker
                        label="Recurring"
                        recurrings={fetchedRecurrings}
                        value={recurringId}
                        onChange={setRecurringId}
                    />
                );
            case 'update':
                return (
                    <div className="space-y-3">
                        <RecurringPicker
                            label="Recurring"
                            recurrings={fetchedRecurrings}
                            value={recurringId}
                            onChange={setRecurringId}
                        />
                        <TextAreaField
                            label="Request Body (JSON)"
                            value={jsonBody}
                            onChange={setJsonBody}
                            placeholder='{"transaction_amount": 5000, ...}'
                        />
                    </div>
                );
            case 'delete':
                return (
                    <div className="space-y-2">
                        <RecurringPicker
                            label="Recurring"
                            recurrings={fetchedRecurrings}
                            value={recurringId}
                            onChange={setRecurringId}
                        />
                        <p className="text-xs text-amber-600">Deleting a recurring cannot be undone.</p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Section title="Recurring" description="Create, view, update, and cancel recurring billing">
            <div className="pt-4 space-y-4">
                <div className="flex gap-2 flex-wrap">
                    {actions.map((a) => (
                        <button
                            key={a}
                            onClick={() => {
                                setAction(a);
                                endpoint.reset();
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                action === a
                                    ? 'bg-mld-admin-primary text-white'
                                    : 'bg-mld-menu-background text-mld-text-secondary hover:bg-mld-panel-border'
                            }`}
                        >
                            {a}
                        </button>
                    ))}
                </div>

                {renderActionForm()}

                <ExecuteButton
                    onClick={handleExecute}
                    loading={endpoint.loading}
                    variant={action === 'delete' ? 'danger' : 'primary'}
                    label={action === 'delete' ? 'Delete' : 'Execute'}
                />

                {renderListTable()}

                {action !== 'list' && (
                    <ResponseDisplay data={endpoint.data} error={endpoint.error} loading={endpoint.loading} />
                )}
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Quick Invoices
// ---------------------------------------------------------------------------

interface InvoiceSummary {
    id: string;
    title: string;
    amount_due: number;
    remaining_balance: number;
    email: string;
    due_date: string;
    status_id: number;
    payment_status_id: number;
    created_ts: number;
}

const INVOICE_STATUS: Record<number, string> = {1: 'Active', 2: 'Closed', 0: 'Draft'};
const INVOICE_PAY_STATUS: Record<number, string> = {1: 'Unpaid', 2: 'Paid', 3: 'Partially Paid'};

function invoiceLabel(inv: InvoiceSummary): string {
    const title = inv.title || 'Untitled';
    const amt = formatCents(inv.amount_due);
    const status = INVOICE_PAY_STATUS[inv.payment_status_id] || `status ${inv.payment_status_id}`;
    return `${title} — ${amt} — ${status}`;
}

function InvoicePicker({
    label,
    invoices,
    value,
    onChange,
}: {
    label: string;
    invoices: InvoiceSummary[];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            {invoices.length > 0 ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                >
                    <option value="">Select an invoice...</option>
                    {invoices.map((inv) => (
                        <option key={inv.id} value={inv.id}>
                            {invoiceLabel(inv)}
                        </option>
                    ))}
                </select>
            ) : (
                <p className="text-sm text-mld-muted-text italic">
                    No invoices loaded. Click &quot;list&quot; first to fetch invoices.
                </p>
            )}
            {value && (
                <p className="mt-1 text-xs text-mld-muted-text font-mono truncate">{value}</p>
            )}
        </div>
    );
}

function InvoicesSection() {
    const [action, setAction] = useState('list');
    const [invoiceId, setInvoiceId] = useState('');
    const [title, setTitle] = useState('');
    const [invoiceAmount, setInvoiceAmount] = useState('10000');
    const [email, setEmail] = useState('');
    const [dueDate, setDueDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 30);
        return d.toISOString().slice(0, 10);
    });
    const [itemName, setItemName] = useState('');
    const [sendEmail, setSendEmail] = useState(false);
    const [fetchedInvoices, setFetchedInvoices] = useState<InvoiceSummary[]>([]);
    const endpoint = useEndpointAction(FortisService.sandboxInvoices);

    // Extract invoice list when list response arrives
    useEffect(() => {
        if (action !== 'list' || !endpoint.data) return;
        const raw = endpoint.data as any;
        const list: any[] = raw?.list || raw?.data || (Array.isArray(raw) ? raw : []);
        const summaries: InvoiceSummary[] = list.map((inv: any) => ({
            id: inv.id || '',
            title: inv.title || '',
            amount_due: inv.amount_due || 0,
            remaining_balance: inv.remaining_balance || 0,
            email: inv.email || '',
            due_date: inv.due_date || '',
            status_id: inv.status_id || 0,
            payment_status_id: inv.payment_status_id || 0,
            created_ts: inv.created_ts || 0,
        }));
        setFetchedInvoices(summaries);
    }, [action, endpoint.data]);

    const handleExecute = () => {
        const body: Record<string, unknown> = {action};
        switch (action) {
            case 'list':
                break;
            case 'create':
                body.data = {
                    title: title || 'Lab Order Invoice',
                    amount_due: Number(invoiceAmount) || 10000,
                    due_date: dueDate,
                    ...(email && {email}),
                    ...(sendEmail && {send_email: true}),
                    ...(itemName && {
                        item_list: [{name: itemName, amount: Number(invoiceAmount) || 10000}],
                    }),
                };
                break;
            case 'get':
                body.invoiceId = invoiceId;
                break;
        }
        endpoint.execute(body);
    };

    const actions = ['list', 'create', 'get'];

    const renderListTable = () => {
        if (action !== 'list' || fetchedInvoices.length === 0) return null;
        return (
            <div className="overflow-x-auto rounded-lg border border-mld-panel-border">
                <table className="w-full text-sm">
                    <thead className="bg-mld-menu-background text-left text-xs uppercase text-mld-muted-text">
                        <tr>
                            <th className="px-3 py-2">Title</th>
                            <th className="px-3 py-2">Amount</th>
                            <th className="px-3 py-2">Balance</th>
                            <th className="px-3 py-2">Email</th>
                            <th className="px-3 py-2">Payment</th>
                            <th className="px-3 py-2">Due</th>
                            <th className="px-3 py-2">ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mld-panel-border">
                        {fetchedInvoices.map((inv) => (
                            <tr key={inv.id} className="hover:bg-mld-menu-background">
                                <td className="px-3 py-2 font-medium text-mld-text-primary">{inv.title || '—'}</td>
                                <td className="px-3 py-2 font-mono text-mld-text-primary">{formatCents(inv.amount_due)}</td>
                                <td className="px-3 py-2 font-mono text-mld-text-secondary text-xs">{formatCents(inv.remaining_balance)}</td>
                                <td className="px-3 py-2 text-mld-text-secondary text-xs truncate max-w-[150px]" title={inv.email}>{inv.email || '—'}</td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                                        inv.payment_status_id === 2
                                            ? 'bg-green-100 text-green-800'
                                            : inv.payment_status_id === 3
                                            ? 'bg-amber-100 text-amber-800'
                                            : 'bg-mld-menu-background text-mld-text-secondary'
                                    }`}>
                                        {INVOICE_PAY_STATUS[inv.payment_status_id] || inv.payment_status_id}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-mld-text-secondary text-xs">{inv.due_date || '—'}</td>
                                <td className="px-3 py-2 text-mld-muted-text font-mono text-xs truncate max-w-[140px]" title={inv.id}>{inv.id}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderActionForm = () => {
        switch (action) {
            case 'list':
                return (
                    <p className="text-sm text-mld-muted-text">
                        Fetch all invoices.
                        {fetchedInvoices.length > 0 && ` ${fetchedInvoices.length} invoice(s) loaded.`}
                    </p>
                );
            case 'create':
                return (
                    <div className="space-y-3 p-4 bg-mld-menu-background rounded-lg border border-mld-panel-border">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FormField label="Title" value={title} onChange={setTitle} placeholder="Lab Order #123" />
                            <FormField label="Amount Due (cents)" value={invoiceAmount} onChange={setInvoiceAmount} type="number" placeholder="10000" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FormField label="Patient Email" value={email} onChange={setEmail} placeholder="patient@example.com" type="email" />
                            <FormField label="Due Date" value={dueDate} onChange={setDueDate} type="date" />
                        </div>
                        <FormField label="Item Description" value={itemName} onChange={setItemName} placeholder="Comprehensive Metabolic Panel" />
                        <ToggleField
                            label="Send email to patient"
                            checked={sendEmail}
                            onChange={setSendEmail}
                            description="Fortis will email the invoice with a payment link"
                        />
                    </div>
                );
            case 'get':
                return (
                    <InvoicePicker
                        label="Invoice"
                        invoices={fetchedInvoices}
                        value={invoiceId}
                        onChange={setInvoiceId}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Section title="Quick Invoices" description="Create and view invoices for lab orders">
            <div className="pt-4 space-y-4">
                <div className="flex gap-2 flex-wrap">
                    {actions.map((a) => (
                        <button
                            key={a}
                            onClick={() => {
                                setAction(a);
                                endpoint.reset();
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                action === a
                                    ? 'bg-mld-admin-primary text-white'
                                    : 'bg-mld-menu-background text-mld-text-secondary hover:bg-mld-panel-border'
                            }`}
                        >
                            {a}
                        </button>
                    ))}
                </div>

                {renderActionForm()}

                <ExecuteButton onClick={handleExecute} loading={endpoint.loading} />

                {renderListTable()}

                {action !== 'list' && (
                    <ResponseDisplay data={endpoint.data} error={endpoint.error} loading={endpoint.loading} />
                )}
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Onboarding
// ---------------------------------------------------------------------------

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
    'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
    'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
    'VT','VA','WA','WV','WI','WY',
];

function OnboardingSection() {
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const endpoint = useEndpointAction(FortisService.sandboxOnboarding);

    const handleExecute = () => {
        endpoint.execute({
            data: {
                ...(companyName && {company_name: companyName}),
                ...(email && {email}),
                ...(phone && {phone_number: phone}),
                ...(street && {street}),
                ...(city && {city}),
                ...(state && {state}),
                ...(zip && {zip}),
            },
        });
    };

    return (
        <Section title="Onboarding" description="Initiate sub-merchant boarding for organizations">
            <div className="pt-4 space-y-4">
                <div className="space-y-3 p-4 bg-mld-menu-background rounded-lg border border-mld-panel-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <FormField label="Company Name" value={companyName} onChange={setCompanyName} placeholder="Acme Health Clinic" />
                        <FormField label="Email" value={email} onChange={setEmail} placeholder="admin@clinic.com" type="email" />
                    </div>
                    <FormField label="Phone" value={phone} onChange={setPhone} placeholder="555-123-4567" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <FormField label="Street" value={street} onChange={setStreet} placeholder="123 Main St" />
                        <FormField label="City" value={city} onChange={setCity} placeholder="Dallas" />
                        <div>
                            <label className="block text-sm font-medium text-mld-text-secondary mb-1">State</label>
                            <select
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                            >
                                <option value="">Select...</option>
                                {US_STATES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <FormField label="Zip" value={zip} onChange={setZip} placeholder="75201" />
                    </div>
                </div>
                <ExecuteButton onClick={handleExecute} loading={endpoint.loading} label="Submit Onboarding" />
                <ResponseDisplay data={endpoint.data} error={endpoint.error} loading={endpoint.loading} />
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Locations
// ---------------------------------------------------------------------------

interface LocationSummary {
    id: string;
    name: string;
    created_ts: number;
}

function LocationPicker({
    label,
    locations,
    value,
    onChange,
}: {
    label: string;
    locations: LocationSummary[];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            {locations.length > 0 ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                >
                    <option value="">Select a location...</option>
                    {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                            {loc.name || loc.id}
                        </option>
                    ))}
                </select>
            ) : (
                <p className="text-sm text-mld-muted-text italic">
                    No locations loaded. Click &quot;list&quot; first to fetch locations.
                </p>
            )}
            {value && (
                <p className="mt-1 text-xs text-mld-muted-text font-mono truncate">{value}</p>
            )}
        </div>
    );
}

function LocationsSection() {
    const [action, setAction] = useState('list');
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [fetchedLocations, setFetchedLocations] = useState<LocationSummary[]>([]);
    const [data, setData] = useState<unknown>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleExecute = async () => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            if (action === 'list') {
                const result = await FortisService.sandboxLocations();
                setData(result);
                // Extract locations for picker
                const raw = result as any;
                const list: any[] = raw?.list || raw?.data || (Array.isArray(raw) ? raw : []);
                const summaries: LocationSummary[] = list.map((loc: any) => ({
                    id: loc.id || '',
                    name: loc.name || '',
                    created_ts: loc.created_ts || 0,
                }));
                setFetchedLocations(summaries);
            } else {
                const result = await FortisService.sandboxLocations(selectedLocationId || undefined);
                setData(result);
            }
        } catch (err: any) {
            setError(err?.data?.error || err?.message || 'Request failed');
        } finally {
            setLoading(false);
        }
    };

    const actions = ['list', 'get'];

    const renderListTable = () => {
        if (action !== 'list' || fetchedLocations.length === 0) return null;
        return (
            <div className="overflow-x-auto rounded-lg border border-mld-panel-border">
                <table className="w-full text-sm">
                    <thead className="bg-mld-menu-background text-left text-xs uppercase text-mld-muted-text">
                        <tr>
                            <th className="px-3 py-2">Name</th>
                            <th className="px-3 py-2">Created</th>
                            <th className="px-3 py-2">ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mld-panel-border">
                        {fetchedLocations.map((loc) => (
                            <tr key={loc.id} className="hover:bg-mld-menu-background">
                                <td className="px-3 py-2 font-medium text-mld-text-primary">{loc.name || '—'}</td>
                                <td className="px-3 py-2 text-mld-muted-text text-xs whitespace-nowrap">{formatTxnDate(loc.created_ts)}</td>
                                <td className="px-3 py-2 text-mld-muted-text font-mono text-xs truncate max-w-[200px]" title={loc.id}>{loc.id}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Section title="Locations" description="List or view Fortis merchant locations">
            <div className="pt-4 space-y-4">
                <div className="flex gap-2 flex-wrap">
                    {actions.map((a) => (
                        <button
                            key={a}
                            onClick={() => {
                                setAction(a);
                                setData(null);
                                setError(null);
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                action === a
                                    ? 'bg-mld-admin-primary text-white'
                                    : 'bg-mld-menu-background text-mld-text-secondary hover:bg-mld-panel-border'
                            }`}
                        >
                            {a}
                        </button>
                    ))}
                </div>

                {action === 'list' && (
                    <p className="text-sm text-mld-muted-text">
                        Fetch all locations.
                        {fetchedLocations.length > 0 && ` ${fetchedLocations.length} location(s) loaded.`}
                    </p>
                )}

                {action === 'get' && (
                    <LocationPicker
                        label="Location"
                        locations={fetchedLocations}
                        value={selectedLocationId}
                        onChange={setSelectedLocationId}
                    />
                )}

                <ExecuteButton onClick={handleExecute} loading={loading} />

                {renderListTable()}

                {action !== 'list' && (
                    <ResponseDisplay data={data} error={error} loading={loading} />
                )}
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Webhooks
// ---------------------------------------------------------------------------

interface WebhookSummary {
    id: string;
    url: string;
    resource: string;
    is_active: boolean;
    on_create: number;
    on_update: number;
    on_delete: number;
    created_ts: number;
}

const WEBHOOK_RESOURCES = ['transactionbatch', 'transaction', 'contact', 'accountvault'];

function webhookLabel(wh: WebhookSummary): string {
    const urlShort = wh.url ? (wh.url.length > 40 ? wh.url.slice(0, 40) + '…' : wh.url) : 'No URL';
    const active = wh.is_active ? 'Active' : 'Inactive';
    return `${wh.resource} — ${urlShort} (${active})`;
}

function WebhookPicker({
    label,
    webhooks,
    value,
    onChange,
}: {
    label: string;
    webhooks: WebhookSummary[];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            {webhooks.length > 0 ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                >
                    <option value="">Select a webhook...</option>
                    {webhooks.map((wh) => (
                        <option key={wh.id} value={wh.id}>
                            {webhookLabel(wh)}
                        </option>
                    ))}
                </select>
            ) : (
                <p className="text-sm text-mld-muted-text italic">No webhooks loaded — run List first.</p>
            )}
        </div>
    );
}

function WebhooksSection() {
    const [action, setAction] = useState('list');
    const [fetchedWebhooks, setFetchedWebhooks] = useState<WebhookSummary[]>([]);
    const [selectedWebhookId, setSelectedWebhookId] = useState('');
    const [data, setData] = useState<unknown>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // Create form fields
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookResource, setWebhookResource] = useState('transactionbatch');
    const [onCreate, setOnCreate] = useState(true);
    const [onUpdate, setOnUpdate] = useState(true);
    const [onDelete, setOnDelete] = useState(true);

    const handleExecute = async () => {
        setLoading(true);
        setError(null);
        setData(null);
        try {
            if (action === 'list') {
                const result = await FortisService.sandboxGetWebhooks();
                setData(result);
                const raw = result as any;
                const list: any[] = raw?.list || raw?.data || (Array.isArray(raw) ? raw : []);
                const summaries: WebhookSummary[] = list.map((wh: any) => ({
                    id: wh.id || '',
                    url: wh.url || '',
                    resource: wh.resource || '',
                    is_active: wh.is_active ?? true,
                    on_create: wh.on_create ?? 0,
                    on_update: wh.on_update ?? 0,
                    on_delete: wh.on_delete ?? 0,
                    created_ts: wh.created_ts || 0,
                }));
                setFetchedWebhooks(summaries);
                if (summaries.length > 0 && !selectedWebhookId) {
                    setSelectedWebhookId(summaries[0].id);
                }
            } else if (action === 'create') {
                const result = await FortisService.sandboxWebhooks({
                    action: 'create',
                    data: {
                        url: webhookUrl,
                        resource: webhookResource,
                        is_active: true,
                        on_create: onCreate ? 1 : 0,
                        on_update: onUpdate ? 1 : 0,
                        on_delete: onDelete ? 1 : 0,
                    },
                });
                setData(result);
            } else if (action === 'delete') {
                const result = await FortisService.sandboxWebhooks({
                    action: 'delete',
                    webhookId: selectedWebhookId,
                });
                setData(result);
                // Remove from local list
                setFetchedWebhooks((prev) => prev.filter((w) => w.id !== selectedWebhookId));
                setSelectedWebhookId('');
            }
        } catch (err: any) {
            setError(err?.data?.error || err?.message || 'Request failed');
        } finally {
            setLoading(false);
        }
    };

    const actions = ['list', 'create', 'delete'];

    const renderListTable = () => {
        if (action !== 'list' || fetchedWebhooks.length === 0) return null;
        return (
            <div className="overflow-x-auto rounded-lg border border-mld-panel-border">
                <table className="w-full text-sm">
                    <thead className="bg-mld-menu-background text-left text-xs uppercase text-mld-muted-text">
                        <tr>
                            <th className="px-3 py-2">URL</th>
                            <th className="px-3 py-2">Resource</th>
                            <th className="px-3 py-2">Active</th>
                            <th className="px-3 py-2 text-center">Create</th>
                            <th className="px-3 py-2 text-center">Update</th>
                            <th className="px-3 py-2 text-center">Delete</th>
                            <th className="px-3 py-2">Created</th>
                            <th className="px-3 py-2">ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mld-panel-border">
                        {fetchedWebhooks.map((wh) => (
                            <tr key={wh.id} className="hover:bg-mld-menu-background">
                                <td className="px-3 py-2 font-medium text-mld-text-primary max-w-[250px] truncate" title={wh.url}>{wh.url || '—'}</td>
                                <td className="px-3 py-2 text-mld-text-secondary">{wh.resource || '—'}</td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${wh.is_active ? 'bg-green-100 text-green-700' : 'bg-mld-menu-background text-mld-muted-text'}`}>
                                        {wh.is_active ? 'Yes' : 'No'}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-center">{wh.on_create ? '✓' : '—'}</td>
                                <td className="px-3 py-2 text-center">{wh.on_update ? '✓' : '—'}</td>
                                <td className="px-3 py-2 text-center">{wh.on_delete ? '✓' : '—'}</td>
                                <td className="px-3 py-2 text-mld-muted-text text-xs whitespace-nowrap">{formatTxnDate(wh.created_ts)}</td>
                                <td className="px-3 py-2 text-mld-muted-text font-mono text-xs truncate max-w-[200px]" title={wh.id}>{wh.id}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Section title="Webhooks" description="View and manage registered Fortis webhooks">
            <div className="pt-4 space-y-4">
                <div className="flex gap-2 flex-wrap">
                    {actions.map((a) => (
                        <button
                            key={a}
                            onClick={() => {
                                setAction(a);
                                setData(null);
                                setError(null);
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                action === a
                                    ? 'bg-mld-admin-primary text-white'
                                    : 'bg-mld-menu-background text-mld-text-secondary hover:bg-mld-panel-border'
                            }`}
                        >
                            {a.charAt(0).toUpperCase() + a.slice(1)}
                        </button>
                    ))}
                </div>

                {action === 'list' && (
                    <p className="text-sm text-mld-muted-text">
                        Fetch all registered webhooks.
                        {fetchedWebhooks.length > 0 && ` ${fetchedWebhooks.length} webhook(s) loaded.`}
                    </p>
                )}

                {action === 'create' && (
                    <div className="space-y-3">
                        <FormField label="Webhook URL" value={webhookUrl} onChange={setWebhookUrl} placeholder="https://your-domain.com/webhooks/fortis" />
                        <div>
                            <label className="block text-sm font-medium text-mld-text-secondary mb-1">Resource</label>
                            <select
                                value={webhookResource}
                                onChange={(e) => setWebhookResource(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                            >
                                {WEBHOOK_RESOURCES.map((r) => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 text-sm text-mld-text-secondary cursor-pointer">
                                <input type="checkbox" checked={onCreate} onChange={(e) => setOnCreate(e.target.checked)} className="rounded border-gray-300 text-mld-admin-primary focus:ring-mld-admin-primary" />
                                On Create
                            </label>
                            <label className="flex items-center gap-2 text-sm text-mld-text-secondary cursor-pointer">
                                <input type="checkbox" checked={onUpdate} onChange={(e) => setOnUpdate(e.target.checked)} className="rounded border-gray-300 text-mld-admin-primary focus:ring-mld-admin-primary" />
                                On Update
                            </label>
                            <label className="flex items-center gap-2 text-sm text-mld-text-secondary cursor-pointer">
                                <input type="checkbox" checked={onDelete} onChange={(e) => setOnDelete(e.target.checked)} className="rounded border-gray-300 text-mld-admin-primary focus:ring-mld-admin-primary" />
                                On Delete
                            </label>
                        </div>
                    </div>
                )}

                {action === 'delete' && (
                    <WebhookPicker
                        label="Webhook"
                        webhooks={fetchedWebhooks}
                        value={selectedWebhookId}
                        onChange={setSelectedWebhookId}
                    />
                )}

                <ExecuteButton
                    onClick={handleExecute}
                    loading={loading}
                    variant={action === 'delete' ? 'danger' : 'primary'}
                    label={action === 'delete' ? 'Delete' : 'Execute'}
                />

                {renderListTable()}

                {action !== 'list' && (
                    <ResponseDisplay data={data} error={error} loading={loading} />
                )}
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Elements (Live Payment Form)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Saved Tokens (localStorage persistence for testing)
// ---------------------------------------------------------------------------

interface SavedToken {
    tokenId: string;
    accountHolderName?: string;
    lastFour?: string;
    expDate?: string;
    paymentMethod?: string;
    cardBrand?: string;
    routingLastFour?: string;
    createdAt: string;
}

const SAVED_TOKENS_KEY = 'fortis_sandbox_tokens';

function fallbackCopy(text: string) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
}

function loadSavedTokens(): SavedToken[] {
    try {
        const raw = localStorage.getItem(SAVED_TOKENS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function persistToken(token: SavedToken) {
    const existing = loadSavedTokens();
    // Avoid duplicates
    if (existing.some((t) => t.tokenId === token.tokenId)) return;
    existing.unshift(token);
    localStorage.setItem(SAVED_TOKENS_KEY, JSON.stringify(existing.slice(0, 20)));
}

function removeSavedToken(tokenId: string) {
    const existing = loadSavedTokens().filter((t) => t.tokenId !== tokenId);
    localStorage.setItem(SAVED_TOKENS_KEY, JSON.stringify(existing));
}

/** Load Commerce.js script once */
function useCommerceScript() {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const scriptId = 'fortis-commercejs';
        const existing = document.getElementById(scriptId);
        if (existing) {
            setReady(true);
            return;
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://js.sandbox.fortis.tech/commercejs-v1.0.0.min.js';
        script.async = true;
        script.onload = () => setReady(true);
        script.onerror = () => console.error('Failed to load Commerce.js');
        document.head.appendChild(script);
    }, []);

    return ready;
}

function ToggleField({
    label,
    checked,
    onChange,
    description,
    disabled,
}: {
    label: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    description?: string;
    disabled?: boolean;
}) {
    return (
        <label className={`flex items-center gap-3 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <div
                onClick={() => !disabled && onChange(!checked)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                    checked ? 'bg-mld-admin-primary' : 'bg-mld-muted-text/40'
                }`}
            >
                <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        checked ? 'translate-x-5' : ''
                    }`}
                />
            </div>
            <div>
                <span className="text-sm font-medium text-mld-text-secondary">{label}</span>
                {description && <p className="text-xs text-mld-muted-text">{description}</p>}
            </div>
        </label>
    );
}

function ElementsSection({sharedTxns}: {sharedTxns: TxnSummary[]}) {
    const commerceReady = useCommerceScript();

    const [txnAction, setTxnAction] = useState('sale');
    const [amount, setAmount] = useState('100');
    const [saveAccount, setSaveAccount] = useState(false);
    const [surcharge, setSurcharge] = useState(false);
    const [includeCc, setIncludeCc] = useState(true);
    const [includeAch, setIncludeAch] = useState(false);
    const [showReceipt, setShowReceipt] = useState(true);

    const [clientToken, setClientToken] = useState<string | null>(null);

    const [paymentResult, setPaymentResult] = useState<Record<string, unknown> | null>(null);
    const [paymentAlert, setPaymentAlert] = useState<{type: 'success' | 'declined' | 'error'; message: string} | null>(null);
    const [formLoaded, setFormLoaded] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [savedTokens, setSavedTokens] = useState<SavedToken[]>(loadSavedTokens);
    const [captureId, setCaptureId] = useState('');

    const containerRef = useRef<HTMLDivElement>(null);
    const elementsRef = useRef<any>(null);

    const endpoint = useEndpointAction(FortisService.sandboxElements);
    const captureEndpoint = useEndpointAction(FortisService.sandboxTransactions);

    // Create intention
    const handleCreateIntention = () => {
        setClientToken(null);
        setPaymentResult(null);
        setPaymentAlert(null);
        setFormLoaded(false);
        setFormError(null);


        const body: Record<string, unknown> = {
            action: 'transaction',
            transaction_action: txnAction,
            amount: Number(amount),
            save_account: saveAccount,
            surcharge,
            include_cc: includeCc,
            include_ach: includeAch,
        };

        endpoint.execute(body);
    };

    // Extract client_token from response
    useEffect(() => {
        if (!endpoint.data) return;
        const resp = endpoint.data as any;
        const token = resp?.data?.client_token ?? resp?.client_token;
        if (token) {
            setClientToken(token);

        } else {
            setFormError('No client_token in response');

        }
    }, [endpoint.data]);

    // Mount Elements widget when client_token is available
    useEffect(() => {
        if (!clientToken || !containerRef.current || !commerceReady) return;

        const Commerce = (window as any).Commerce;
        if (!Commerce) {
            setFormError('Commerce.js not loaded yet. Please wait and try again.');
            return;
        }

        // Clear previous widget and event listeners
        if (elementsRef.current?.cleanup) elementsRef.current.cleanup();
        containerRef.current.innerHTML = '';
        elementsRef.current = null;
        setFormLoaded(false);

        try {
            const elements = new Commerce.elements(clientToken);
            elements.create({
                container: '#fortis-elements-container',
                theme: 'default',
                showReceipt,
            });

            // Listen to all known event names — Fortis docs say 'paymentFinished'
            // but the SDK may use different names
            const handleResult = (result: any) => {
                console.log('[Fortis Elements] event result:', result);
                setPaymentResult(result);

                // Detect approved vs declined from status_code
                const d = result?.data || result;
                const statusCode = d?.status_code ?? result?.status_code;
                const verbiage = d?.verbiage || result?.verbiage || '';
                const amount = d?.transaction_amount ?? result?.transaction_amount;
                const amtStr = typeof amount === 'number' ? ` (${formatCents(amount)})` : '';

                if (statusCode !== undefined) {
                    if (statusCode >= 300) {
                        setPaymentAlert({type: 'declined', message: `Transaction Declined — ${verbiage || `status ${statusCode}`}${amtStr}`});
                    } else if (statusCode >= 200) {
                        setPaymentAlert({type: 'error', message: `Transaction Error — ${verbiage || `status ${statusCode}`}${amtStr}`});
                    } else if (statusCode >= 100) {
                        setPaymentAlert({type: 'success', message: `Transaction Approved — ${verbiage || `status ${statusCode}`}${amtStr}`});
                    }
                }

                // When save_account is true, the transaction response includes a
                // token_id / account_vault_id that can be used for future charges.
                const tokenId =
                    d?.token_id || d?.account_vault_id ||
                    result?.token_id || result?.account_vault_id;
                if (tokenId) {
                    const token: SavedToken = {
                        tokenId,
                        accountHolderName: d?.account_holder_name || d?.first_name,
                        lastFour: d?.last_four,
                        expDate: d?.exp_date,
                        paymentMethod: d?.payment_method || 'cc',
                        cardBrand: d?.account_type,
                        routingLastFour: d?.routing_last_four,
                        createdAt: new Date().toLocaleString(),
                    };
                    persistToken(token);
                    setSavedTokens(loadSavedTokens());
                }
            };

            // Try all possible event names
            elements.on('paymentFinished', handleResult);
            elements.on('done', handleResult);
            elements.on('success', handleResult);
            elements.on('complete', handleResult);
            elements.on('tokenCreated', handleResult);

            elements.on('ready', () => {
                setFormLoaded(true);
            });

            // Also listen for postMessage from the Fortis iframe
            const handleMessage = (event: MessageEvent) => {
                if (typeof event.origin !== 'string' ||
                    (!event.origin.includes('fortis.tech') && !event.origin.includes('fortispay'))) {
                    return;
                }

                let parsed: any = event.data;
                if (typeof parsed === 'string') {
                    try { parsed = JSON.parse(parsed); } catch { return; }
                }
                if (!parsed || typeof parsed !== 'object') return;

                // Only process actual transaction results, ignore lifecycle events.
                // Transaction result: has "@action" + "id" + transaction fields
                let resultData: any = null;

                if (parsed['@action'] && parsed.id && parsed.transaction_amount !== undefined) {
                    resultData = parsed;
                }

                if (resultData) {
                    console.log('[Fortis postMessage] result:', resultData);
                    handleResult(resultData);
                }
            };
            window.addEventListener('message', handleMessage);

            const cleanup = () => window.removeEventListener('message', handleMessage);
            elementsRef.current = {elements, cleanup};
            // Set loaded after a short delay in case 'ready' event doesn't fire
            setTimeout(() => setFormLoaded(true), 2000);
        } catch (err: any) {
            setFormError(err?.message || 'Failed to initialize Elements');
        }
    }, [clientToken, commerceReady, showReceipt]);

    const handleCancelForm = () => {
        // Clean up existing Elements
        if (elementsRef.current) {
            const ref = elementsRef.current as any;
            if (typeof ref.cleanup === 'function') ref.cleanup();
            else if (typeof ref.elements?.dispose === 'function') ref.elements.dispose();
            elementsRef.current = null;
        }
        setClientToken(null);
        setFormLoaded(false);
        setFormError(null);
    };

    const txnActions = ['sale', 'auth-only', 'avs-only', 'refund', 'capture'];
    const formLocked = !!clientToken;

    return (
        <Section
            title="Make Payment"
            description="Render the Fortis Elements payment widget"
        >
            <div className="pt-4 space-y-5">
                {/* Transaction config */}
                <div className={`space-y-4 p-4 bg-mld-menu-background rounded-lg border border-mld-panel-border ${formLocked ? 'opacity-60' : ''}`}>
                        <div>
                            <label className="block text-sm font-medium text-mld-text-secondary mb-2">Action</label>
                            <div className="flex gap-2 flex-wrap">
                                {txnActions.map((a) => (
                                    <button
                                        key={a}
                                        disabled={formLocked}
                                        onClick={() => setTxnAction(a)}
                                        className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                            txnAction === a
                                                ? 'bg-mld-text-primary text-white'
                                                : 'bg-white text-mld-text-secondary border border-mld-panel-border hover:bg-mld-menu-background'
                                        } ${formLocked ? 'cursor-not-allowed' : ''}`}
                                    >
                                        {a}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <FormField
                            label="Amount (cents)"
                            value={amount}
                            onChange={setAmount}
                            placeholder="100"
                            type="number"
                            disabled={formLocked}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                                <p className="text-sm font-medium text-mld-text-secondary">Payment Methods</p>
                                <ToggleField
                                    label="Credit Card"
                                    checked={includeCc}
                                    onChange={setIncludeCc}
                                    disabled={formLocked}
                                />
                                {includeCc && (
                                    <div className="ml-13 pl-4 border-l-2 border-mld-panel-border">
                                        <ToggleField
                                            label="Surcharge"
                                            checked={surcharge}
                                            onChange={setSurcharge}
                                            description="Uses surcharge product transaction ID"
                                            disabled={formLocked}
                                        />
                                    </div>
                                )}
                                <ToggleField
                                    label="ACH / Bank Account"
                                    checked={includeAch}
                                    onChange={setIncludeAch}
                                    description="No surcharge"
                                    disabled={formLocked}
                                />
                            </div>

                            <div className="space-y-3">
                                <p className="text-sm font-medium text-mld-text-secondary">Options</p>
                                <ToggleField
                                    label="Save card info for later use"
                                    checked={saveAccount}
                                    onChange={setSaveAccount}
                                    description="Stores a reusable token after payment"
                                    disabled={formLocked}
                                />
                                <ToggleField
                                    label="Show Receipt"
                                    checked={showReceipt}
                                    onChange={setShowReceipt}
                                    description="Display receipt after transaction"
                                    disabled={formLocked}
                                />
                            </div>
                        </div>
                        {formLocked && (
                            <div className="flex items-center justify-between">
                                <p className="text-xs text-mld-muted-text italic">Options are locked while the payment form is active.</p>
                                <button
                                    onClick={handleCancelForm}
                                    className="text-xs text-rose-600 hover:text-rose-800 font-medium underline"
                                >
                                    Cancel Form &amp; Unlock
                                </button>
                            </div>
                        )}
                </div>

                {/* Capture mode — just show transaction picker */}
                {txnAction === 'capture' && (
                    <div className="space-y-3 p-4 bg-mld-menu-background rounded-lg border border-mld-panel-border">
                        {(() => {
                            const captureCandidates = sharedTxns.filter((t) => !NEGATIVE_TYPE_IDS.has(t.type_id));
                            return (
                                <TransactionPicker
                                    label="Transaction to capture"
                                    transactions={captureCandidates}
                                    value={captureId}
                                    onChange={setCaptureId}
                                />
                            );
                        })()}
                    </div>
                )}

                {/* Commerce.js status */}
                {txnAction !== 'capture' && !commerceReady && (
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading Commerce.js SDK...
                    </div>
                )}

                {txnAction === 'capture' ? (
                    <ExecuteButton
                        onClick={() => {
                            if (!captureId) return;
                            captureEndpoint.execute({action: 'capture', transactionId: captureId});
                        }}
                        loading={captureEndpoint.loading}
                    />
                ) : (
                    <ExecuteButton
                        onClick={handleCreateIntention}
                        loading={endpoint.loading}
                        label="Load Form"
                    />
                )}

                {/* Capture response */}
                {txnAction === 'capture' && (
                    <ResponseDisplay data={captureEndpoint.data} error={captureEndpoint.error} loading={captureEndpoint.loading} />
                )}

                {/* Error from endpoint */}
                {txnAction !== 'capture' && endpoint.error && (
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-md">
                        <p className="text-sm font-medium text-rose-700">API Error</p>
                        <pre className="text-xs text-rose-600 mt-1 whitespace-pre-wrap break-all">
                            {endpoint.error}
                        </pre>
                    </div>
                )}

                {/* Form error */}
                {formError && (
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-md">
                        <p className="text-sm text-rose-700">{formError}</p>
                    </div>
                )}

                {/* Live Elements widget container */}
                {clientToken && (
                    <div className="border-2 border-dashed border-mld-admin-primary rounded-lg p-1 bg-white">
                        <div className="px-3 py-2 bg-mld-admin-primary-50 rounded-t text-sm font-medium text-mld-admin-primary-dark flex items-center justify-between">
                            <span>Fortis Elements — Live Sandbox Form</span>
                            <div className="flex items-center gap-3">
                                {!formLoaded && (
                                    <span className="flex items-center gap-1 text-xs text-mld-muted-text">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Rendering...
                                    </span>
                                )}
                                <button
                                    onClick={handleCancelForm}
                                    className="text-xs text-rose-600 hover:text-rose-800 font-medium px-2 py-0.5 rounded border border-rose-300 hover:bg-rose-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                        <div
                            id="fortis-elements-container"
                            ref={containerRef}
                            className="min-h-[200px]"
                        />
                    </div>
                )}

                {/* Payment status alert */}
                {paymentAlert && (
                    <div className={`p-3 rounded-md border ${
                        paymentAlert.type === 'success'
                            ? 'bg-green-50 border-green-200'
                            : 'bg-rose-50 border-rose-200'
                    }`}>
                        <p className={`text-sm font-medium ${
                            paymentAlert.type === 'success' ? 'text-green-700' : 'text-rose-700'
                        }`}>
                            {paymentAlert.message}
                        </p>
                    </div>
                )}

                {/* Payment result */}
                {paymentResult && (
                    <details className="text-sm">
                        <summary className="cursor-pointer font-medium text-mld-text-secondary hover:text-mld-text-primary">
                            JSON response
                        </summary>
                        <pre className="mt-2 p-3 bg-mld-menu-background border border-mld-panel-border rounded-md text-xs text-mld-text-secondary whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                            {JSON.stringify(paymentResult, null, 2)}
                        </pre>
                    </details>
                )}

                {/* Test reference: cards + saved tokens */}
                <details className="text-sm">
                    <summary className="cursor-pointer font-medium text-mld-muted-text hover:text-mld-text-secondary">
                        Sandbox Test Cards & Saved Tokens
                    </summary>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Test card numbers */}
                        <div className="p-3 bg-mld-menu-background border border-mld-panel-border rounded-md text-xs text-mld-text-secondary space-y-1">
                            <p className="font-semibold text-mld-text-secondary mb-2">Test Card Numbers</p>
                            <p><strong>Visa:</strong> 4111 1111 1111 1111 &nbsp; Exp: 12/27 &nbsp; CVV: 999</p>
                            <p><strong>Visa Corp:</strong> 4005 5622 3121 2149 &nbsp; Exp: 12/27 &nbsp; CVV: 999</p>
                            <p><strong>Mastercard:</strong> 5454 5454 5454 5454 &nbsp; Exp: 12/27 &nbsp; CVV: 999</p>
                            <p><strong>Amex:</strong> 3714 496353 98431 &nbsp; Exp: 12/27 &nbsp; CVV: 1234</p>
                            <p><strong>Discover:</strong> 6011 0009 9550 0000 &nbsp; Exp: 12/27 &nbsp; CVV: 999</p>
                            <p className="mt-2"><strong>ACH Routing:</strong> 072000326 &nbsp; <strong>Account:</strong> any 4-17 digits</p>
                            <p className="mt-2 text-mld-muted-text"><strong>Decline:</strong> $5.00 &nbsp; <strong>Partial auth:</strong> $2.01 &nbsp; <strong>Delay:</strong> $8.XX</p>
                        </div>

                        {/* Saved tokens — captured from Elements postMessage */}
                        <div className="p-3 bg-mld-menu-background border border-mld-panel-border rounded-md text-xs text-mld-text-secondary">
                            <div className="flex items-center justify-between mb-2">
                                <p className="font-semibold text-mld-text-secondary">Saved Tokens (from Elements)</p>
                                {savedTokens.length > 0 && (
                                    <button
                                        onClick={() => {
                                            localStorage.removeItem(SAVED_TOKENS_KEY);
                                            setSavedTokens([]);
                                        }}
                                        className="text-rose-500 hover:text-rose-700 flex items-center gap-1"
                                    >
                                        <Trash2 className="w-3 h-3" /> Clear all
                                    </button>
                                )}
                            </div>
                            {savedTokens.length === 0 ? (
                                <p className="text-mld-muted-text italic">
                                    No tokens saved yet. Run a transaction with &quot;Save card info&quot; enabled.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {savedTokens.map((t) => (
                                        <div
                                            key={t.tokenId}
                                            className="flex items-center justify-between gap-2 py-1.5 border-b border-mld-panel-border last:border-0"
                                        >
                                            <div className="min-w-0">
                                                <p>
                                                    {t.accountHolderName && (
                                                        <span className="text-mld-text-secondary">{t.accountHolderName} — </span>
                                                    )}
                                                    <strong className="text-mld-text-primary">
                                                        {(t.paymentMethod || 'cc').toUpperCase()}
                                                        {t.cardBrand ? ` (${t.cardBrand})` : ''}
                                                        {t.lastFour ? ` ****${t.lastFour}` : ''}
                                                    </strong>
                                                    {t.expDate && (
                                                        <span className="text-mld-muted-text ml-1">
                                                            exp {t.expDate.slice(0, 2)}/{t.expDate.slice(2)}
                                                        </span>
                                                    )}
                                                    {t.routingLastFour && (
                                                        <span className="text-mld-muted-text ml-1">
                                                            routing ****{t.routingLastFour}
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="font-mono text-mld-muted-text truncate" title={t.tokenId}>
                                                    {t.tokenId}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <button
                                                    onClick={() => {
                                                        if (navigator.clipboard?.writeText) {
                                                            navigator.clipboard.writeText(t.tokenId).catch(() => {
                                                                fallbackCopy(t.tokenId);
                                                            });
                                                        } else {
                                                            fallbackCopy(t.tokenId);
                                                        }
                                                    }}
                                                    className="text-mld-admin-primary hover:text-mld-admin-primary-dark px-1"
                                                    title="Copy token ID"
                                                >
                                                    Copy
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        removeSavedToken(t.tokenId);
                                                        setSavedTokens(loadSavedTokens());
                                                    }}
                                                    className="text-rose-400 hover:text-rose-600 px-1"
                                                    title="Remove"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </details>
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Section: Terminals
// ---------------------------------------------------------------------------

interface TerminalSummary {
    id: string;
    title: string;
    serial_number: string;
    location_id: string;
    active: boolean;
    terminal_number: string;
    mac_address: string;
    local_ip_address: string;
}

function terminalLabel(t: TerminalSummary): string {
    const name = t.title || t.serial_number || t.id;
    const serial = t.serial_number ? ` (S/N ${t.serial_number})` : '';
    return `${name}${serial}`;
}

function TerminalPicker({
    label,
    terminals,
    value,
    onChange,
}: {
    label: string;
    terminals: TerminalSummary[];
    value: string;
    onChange: (id: string) => void;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-mld-text-secondary mb-1">{label}</label>
            {terminals.length > 0 ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-mld-admin-primary focus:border-mld-admin-primary"
                >
                    <option value="">Select a terminal...</option>
                    {terminals.map((t) => (
                        <option key={t.id} value={t.id}>
                            {terminalLabel(t)} — {t.active ? 'Active' : 'Inactive'}
                        </option>
                    ))}
                </select>
            ) : (
                <p className="text-sm text-mld-muted-text italic">
                    No terminals loaded. Click &quot;list&quot; first to fetch terminals.
                </p>
            )}
            {value && (
                <p className="mt-1 text-xs text-mld-muted-text font-mono truncate">{value}</p>
            )}
        </div>
    );
}

function TerminalsSection({sharedTxns}: {sharedTxns: TxnSummary[]}) {
    const [action, setAction] = useState('list');
    const [selectedTerminalId, setSelectedTerminalId] = useState('');
    const [amount, setAmount] = useState('100');
    const [description, setDescription] = useState('');
    const [captureId, setCaptureId] = useState('');
    const [fetchedTerminals, setFetchedTerminals] = useState<TerminalSummary[]>([]);
    const endpoint = useEndpointAction(FortisService.sandboxTerminals);
    const captureEndpoint = useEndpointAction(FortisService.sandboxTransactions);

    // Extract terminal list when list response arrives
    useEffect(() => {
        if (action !== 'list' || !endpoint.data) return;
        const raw = endpoint.data as any;
        const list: any[] = raw?.list || raw?.data || (Array.isArray(raw) ? raw : []);
        const summaries: TerminalSummary[] = list.map((t: any) => ({
            id: t.id || '',
            title: t.title || '',
            serial_number: t.serial_number || '',
            location_id: t.location_id || '',
            active: t.active === 1 || t.active === true,
            terminal_number: t.terminal_number || '',
            mac_address: t.mac_address || '',
            local_ip_address: t.local_ip_address || '',
        }));
        setFetchedTerminals(summaries);
    }, [action, endpoint.data]);

    const effectiveTerminalId = selectedTerminalId || (fetchedTerminals.length > 0 ? fetchedTerminals[0].id : '');

    const handleExecute = () => {
        if (action === 'capture') {
            if (!captureId) return;
            captureEndpoint.execute({action: 'capture', transactionId: captureId});
            return;
        }

        const body: Record<string, unknown> = {action};

        switch (action) {
            case 'list':
                break;
            case 'get':
                body.terminalId = effectiveTerminalId;
                break;
            case 'cc-sale':
            case 'cc-auth-only':
                body.terminalId = effectiveTerminalId;
                body.data = {
                    transaction_amount: Number(amount) || 100,
                    ...(description && {description}),
                };
                break;
        }
        endpoint.execute(body);
    };

    const actions = ['list', 'get', 'cc-sale', 'cc-auth-only', 'capture'];

    const renderListTable = () => {
        if (action !== 'list' || fetchedTerminals.length === 0) return null;
        return (
            <div className="overflow-x-auto rounded-lg border border-mld-panel-border">
                <table className="w-full text-sm">
                    <thead className="bg-mld-menu-background text-left text-xs uppercase text-mld-muted-text">
                        <tr>
                            <th className="px-3 py-2">Title</th>
                            <th className="px-3 py-2">Serial Number</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Terminal #</th>
                            <th className="px-3 py-2">IP Address</th>
                            <th className="px-3 py-2">ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mld-panel-border">
                        {fetchedTerminals.map((t) => (
                            <tr key={t.id} className="hover:bg-mld-menu-background">
                                <td className="px-3 py-2 font-medium text-mld-text-primary">
                                    {t.title || '—'}
                                </td>
                                <td className="px-3 py-2 text-mld-text-secondary font-mono text-xs">
                                    {t.serial_number || '—'}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                                        t.active
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-mld-menu-background text-mld-muted-text'
                                    }`}>
                                        {t.active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-mld-text-secondary text-xs">
                                    {t.terminal_number || '—'}
                                </td>
                                <td className="px-3 py-2 text-mld-muted-text font-mono text-xs">
                                    {t.local_ip_address || '—'}
                                </td>
                                <td className="px-3 py-2 text-mld-muted-text font-mono text-xs truncate max-w-[140px]" title={t.id}>
                                    {t.id}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderActionForm = () => {
        switch (action) {
            case 'list':
                return (
                    <p className="text-sm text-mld-muted-text">
                        Fetch all terminals.
                        {fetchedTerminals.length > 0 && ` ${fetchedTerminals.length} terminal(s) loaded.`}
                    </p>
                );
            case 'get':
                return (
                    <TerminalPicker
                        label="Terminal"
                        terminals={fetchedTerminals}
                        value={selectedTerminalId}
                        onChange={setSelectedTerminalId}
                    />
                );
            case 'cc-sale':
            case 'cc-auth-only':
                return (
                    <div className="space-y-3">
                        <TerminalPicker
                            label="Terminal"
                            terminals={fetchedTerminals}
                            value={selectedTerminalId}
                            onChange={setSelectedTerminalId}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FormField
                                label="Amount (cents)"
                                value={amount}
                                onChange={setAmount}
                                placeholder="100"
                                type="number"
                            />
                            <FormField
                                label="Description"
                                value={description}
                                onChange={setDescription}
                                placeholder="Test terminal charge"
                            />
                        </div>
                    </div>
                );
            case 'capture': {
                const captureCandidates = sharedTxns.filter((t) => !NEGATIVE_TYPE_IDS.has(t.type_id));
                return (
                    <TransactionPicker
                        label="Transaction to capture"
                        transactions={captureCandidates}
                        value={captureId}
                        onChange={setCaptureId}
                    />
                );
            }
            default:
                return null;
        }
    };

    return (
        <Section title="Terminals" description="Physical terminal operations — get info, list, CC sale, auth-only">
            <div className="pt-4 space-y-4">
                <div className="flex gap-2 flex-wrap">
                    {actions.map((a) => (
                        <button
                            key={a}
                            onClick={() => {
                                setAction(a);
                                endpoint.reset();
                                captureEndpoint.reset();
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                                action === a
                                    ? 'bg-mld-admin-primary text-white'
                                    : 'bg-mld-menu-background text-mld-text-secondary hover:bg-mld-panel-border'
                            }`}
                        >
                            {a}
                        </button>
                    ))}
                </div>

                {renderActionForm()}

                <ExecuteButton
                    onClick={handleExecute}
                    loading={action === 'capture' ? captureEndpoint.loading : endpoint.loading}
                />

                {renderListTable()}

                {action === 'capture' ? (
                    <ResponseDisplay data={captureEndpoint.data} error={captureEndpoint.error} loading={captureEndpoint.loading} />
                ) : action !== 'list' ? (
                    <ResponseDisplay data={endpoint.data} error={endpoint.error} loading={endpoint.loading} />
                ) : null}
            </div>
        </Section>
    );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const AdminFortisIntegration: React.FC = () => {
    const [sharedTxns, setSharedTxns] = useState<TxnSummary[]>([]);

    return (
        <div className="space-y-4">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-mld-text-primary">Fortis Integration</h1>
                <p className="text-sm text-mld-muted-text mt-1">
                    Test and demo all Fortis merchant services endpoints against the sandbox environment.
                </p>
            </div>

            <ConnectionStatusSection />
            <ContactsSection />
            <ElementsSection sharedTxns={sharedTxns} />
            <TransactionsSection onTxnsFetched={setSharedTxns} />
            <TerminalsSection sharedTxns={sharedTxns} />
            <RecurringSection />
            <InvoicesSection />
            <OnboardingSection />
            <WebhooksSection />
            <LocationsSection />
        </div>
    );
};

export default AdminFortisIntegration;
